import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { PublicStats } from "@/components/PublicStats";
import EtudiantsEn from "@/pages/EtudiantsEn";
import {
  Sparkles,
  ArrowRight,
  Check,
  GraduationCap,
  FileText,
  BookOpen,
  Clock,
  Target,
  Layers,
  Globe,
} from "lucide-react";

/**
 * SEO landing page targeting French-speaking students & researchers.
 *
 * URL: /etudiants — indexed by Google on queries like "résumer thèse IA",
 * "résumer article scientifique", "outil résumé cours PDF", etc.
 *
 * Keeps the same visual language as About.jsx (Swiss + Royal Blue + orange
 * accents) but is more copy-forward: 3 real-life use cases, quantified
 * time savings, direct CTA.
 */

export const HeroStat = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <div
      className="text-4xl md:text-5xl font-black text-[#002FA7]"
      style={{ fontFamily: "Outfit" }}
    >
      {value}
    </div>
    <div className="sux-marker">{label}</div>
  </div>
);

export const UseCase = ({ icon: Icon, tag, title, story, prompt, gain, testid, howLabel = "Comment ça se passe" }) => (
  <div
    className="border border-border bg-card"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 px-6 py-4 border-b border-border bg-secondary/40">
      <div className="w-8 h-8 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="sux-marker">{tag}</div>
    </div>
    <div className="p-6 md:p-8">
      <h3
        className="text-2xl md:text-3xl font-black tracking-tight mb-4 leading-tight"
        style={{ fontFamily: "Outfit" }}
      >
        {title}
      </h3>
      <p className="text-base leading-relaxed mb-5 text-muted-foreground">
        {story}
      </p>
      <div className="border-l-2 border-[#002FA7] pl-4 py-1 mb-5">
        <div className="sux-marker mb-1">{howLabel}</div>
        <p className="text-sm leading-relaxed">{prompt}</p>
      </div>
      <div className="flex items-center gap-2 text-sm font-semibold text-[#002FA7]">
        <Clock className="w-4 h-4" />
        {gain}
      </div>
    </div>
  </div>
);

export default function Etudiants() {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage === "en" ? <EtudiantsEn /> : <EtudiantsFr />;
}

function EtudiantsFr() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const isEn = i18n.resolvedLanguage === "en";
  const guideHref = isEn ? "/guide-en.html" : "/guide.html";

  // SEO: set page title + description on mount so Google indexes this
  // landing with keywords matching student/researcher queries.
  useEffect(() => {
    const previousTitle = document.title;
    const previousDesc = document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content");

    document.title =
      "SUX1 pour Étudiants & Chercheurs — Résumer une thèse, un article, un cours en 5 secondes";

    const descMeta = document.querySelector('meta[name="description"]');
    const descContent =
      "L'outil de résumé IA pour étudiants et chercheurs. Résumez une thèse de 200 pages, un article scientifique ou un cours entier en 5 secondes. PDF, EPUB, URL. Multi-modèle (Claude, GPT, Gemini). Dès 2 € les 20 résumés, sans abonnement.";
    if (descMeta) descMeta.setAttribute("content", descContent);

    return () => {
      document.title = previousTitle;
      if (descMeta && previousDesc) {
        descMeta.setAttribute("content", previousDesc);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-card">
      {/* Hero */}
      <section
        className="border-b border-border bg-gradient-to-b from-secondary/30 to-white"
        data-testid="etudiants-hero"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 lg:py-28">
          <div className="sux-marker mb-4 flex items-center gap-2">
            <GraduationCap className="w-3.5 h-3.5" />
            POUR ÉTUDIANTS · CHERCHEURS · DOCTORANTS
          </div>
          <h1
            className="text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mb-6"
            style={{ fontFamily: "Outfit" }}
          >
            Résumez une thèse.
            <br />
            <span className="text-[#002FA7]">En 5 secondes.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-10">
            Vous lisez 30 articles par semaine. Vous préparez un partiel sur
            un polycopié de 400 pages. Vous digérez une bibliographie de
            thèse. <strong className="text-foreground">SUX1 fait tout ça pour vous</strong> —
            un PDF, un EPUB, une URL, un clic, un résumé fidèle. Exemples
            gratuits, puis dès 2 € les 20 résumés. Pas d'abonnement.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Link
              to={user ? "/" : "/signup"}
              data-testid="etudiants-cta-signup"
              className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#00227A] transition-colors flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Voir les exemples gratuits
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/about"
              data-testid="etudiants-cta-about"
              className="bg-card text-foreground border border-border font-mono uppercase tracking-[0.25em] text-xs px-6 py-4 hover:bg-[#FF2A00] hover:text-white transition-colors"
            >
              Voir tout ce que SUX1 sait faire
            </Link>
          </div>
          <div className="mb-14 flex flex-wrap items-center gap-x-6 gap-y-2">
            <a
              href="/education-plaquette.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="etudiants-cta-plaquette"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] hover:underline"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Télécharger la plaquette PDF (1 page, imprimable)
              <ArrowRight className="w-3 h-3" />
            </a>
            <a
              href={guideHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="etudiants-cta-guide"
              className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] hover:underline"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Guide d'utilisation complet (2 pages, imprimable)
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>

          <PublicStats variant="hero" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <HeroStat label="Pages avalées" value="200+" />
            <HeroStat label="Secondes chrono" value="5" />
            <HeroStat label="Exemples gratuits" value="6" />
            <HeroStat label="20 résumés · pas d'abo" value="2 €" />
          </div>
        </div>
      </section>

      {/* Problem — why ChatGPT alone fails you */}
      <section
        className="border-b border-border bg-white"
        data-testid="etudiants-problem"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">01 · LE VRAI PROBLÈME</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            « J'ai ChatGPT gratuit. Pourquoi j'aurais besoin d'autre chose ? »
          </h2>
          <p className="text-base leading-relaxed mb-4">
            Vous avez essayé. Vous avez collé les 40 premières pages de votre
            polycopié. ChatGPT a répondu — puis a coupé la conversation
            silencieusement à la moitié. Vous avez relancé, vous avez découpé
            en morceaux, vous avez re-prompté. Vous y avez passé une heure.
          </p>
          <p className="text-base leading-relaxed mb-4">
            Le problème n'est pas que ChatGPT soit mauvais. Le problème est
            qu'il n'a <strong className="text-foreground">pas été conçu pour ça</strong>.
            C'est un assistant conversationnel généraliste. Vous, vous avez besoin
            d'un <strong className="text-foreground">outil de résumé industriel</strong> :
            un PDF de 200 pages avalé d'un coup, un article scientifique
            extrait proprement depuis son URL, un livre entier découpé et
            re-synthétisé automatiquement.
          </p>
          <p className="text-base leading-relaxed">
            SUX1 fait exactement cette seule chose — mais il la fait bien.
            Comparaison honnête sur la{" "}
            <Link to="/about" className="text-[#002FA7] underline">
              page À propos
            </Link>
            .
          </p>
        </div>
      </section>

      {/* 3 concrete use cases */}
      <section
        className="border-b border-border bg-secondary/30"
        data-testid="etudiants-usecases"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">02 · TROIS CAS CONCRETS</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-10 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Ce que vous allez faire dès aujourd'hui.
          </h2>

          <div className="space-y-8">
            <UseCase
              testid="etudiants-usecase-1"
              icon={FileText}
              tag="CAS N°1 · ARTICLES SCIENTIFIQUES"
              title="Digérer 30 articles par semaine sans y passer le week-end."
              story={
                "Vous préparez une revue de littérature. Vous avez 30 PDF ouverts dans Zotero. À raison de 45 minutes par article, ça fait 22 heures de lecture. Vous n'avez pas 22 heures."
              }
              prompt={
                "Vous glissez un PDF dans SUX1. Vous choisissez le format « Points clés · Long », langue française forcée. En 5 secondes vous avez : la thèse centrale, la méthodologie, les résultats, les limites, les 3 citations les plus fortes — tout ce qu'il faut pour décider si l'article mérite une lecture approfondie ou une simple mention dans votre biblio."
              }
              gain="Vous passez de 22 h à 45 min sur la revue de littérature."
            />

            <UseCase
              testid="etudiants-usecase-2"
              icon={BookOpen}
              tag="CAS N°2 · POLYCOPIÉ DE COURS"
              title="Préparer un partiel à partir d'un polycopié de 400 pages."
              story={
                "Partiel dans 3 jours. Le prof a filé un polycopié de 400 pages. Vous n'aurez pas le temps de tout lire. Vous avez besoin d'une fiche de synthèse — mais pas d'un truc générique. Une fiche qui garde la structure du cours, les définitions exactes, les théorèmes clés."
              }
              prompt={
                "Vous téléversez le PDF dans SUX1. Mode « Résumé par chapitre » activé. SUX1 détecte automatiquement les chapitres (jusqu'à 40), résume chacun en accordéon, et vous laisse choisir la longueur. Bonus : la lecture vocale native transforme votre fiche en podcast pendant vos trajets."
              }
              gain="Une fiche de révision complète en 5 minutes au lieu d'une semaine."
            />

            <UseCase
              testid="etudiants-usecase-3"
              icon={Target}
              tag="CAS N°3 · EXERCICES ET SUJETS D'EXAMEN"
              title="Comprendre un exercice bloquant à 22 h, la veille du DS."
              story={
                "Vous bloquez sur l'exercice 12 depuis une heure. Le corrigé n'est pas dispo. Vos potes ne répondent pas sur le groupe WhatsApp. Vous ne voulez pas juste la réponse — vous voulez comprendre la méthode."
              }
              prompt={
                "Vous prenez l'énoncé en photo. SUX1 avec le format « Explication & Résolution » (pas dispo sur ChatGPT sans re-prompter) déroule chaque étape avec la justification, l'unité, et les erreurs classiques à éviter. C'est comme avoir un prof particulier disponible à 22 h."
              }
              gain="Le déblocage en 30 secondes, la méthode retenue durablement."
            />

            <UseCase
              testid="etudiants-usecase-4"
              icon={Target}
              tag="CAS N°4 · MÉMORISER SANS S'ENDORMIR"
              title="Retenir une leçon impossible à apprendre par cœur."
              story={
                "Contrôle demain sur le cycle de l'eau, ou les dates de la Révolution. Vous relisez la fiche pour la dixième fois, rien ne rentre. Vous retenez pourtant sans effort les paroles de vos chansons préférées."
              }
              prompt={
                "Vous tapez juste la notion dans SUX1 et choisissez le format « Poème et récitation » : la leçon devient un poème rimé, suivi de « Ce qu'il faut retenir ». Le bouton « Écouter le poème » le lit lentement, puis strophe par strophe (Répéter, Suivante). Une notion abstraite ? Le format « Métaphores » l'explique avec des images du quotidien (« C'est comme… »)."
              }
              gain="La leçon retenue comme une chanson, en 1 crédit (l'écoute est gratuite)."
            />
          </div>
        </div>
      </section>

      {/* Why SUX1 specifically for you */}
      <section
        className="border-b border-border bg-white"
        data-testid="etudiants-why"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">03 · POURQUOI VOUS, EN PARTICULIER</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Cinq raisons qui n'existent pas ailleurs.
          </h2>
          <ul className="space-y-4">
            {[
              [
                "Livre entier en un clic",
                "Pipeline map-reduce en streaming : jusqu'à 300 000 caractères d'un coup. Un manuel de biologie, une thèse, un rapport de stage — tout passe.",
              ],
              [
                "EPUB natif — pas juste PDF",
                "Vos manuels sont en EPUB ? SUX1 les avale directement. Project Gutenberg, e-books de bibliothèque universitaire, tout ce que vous téléchargez légalement.",
              ],
              [
                "URL d'article scientifique",
                "Collez le lien HAL, Cairn, ScienceDirect ou n'importe quel article web : SUX1 extrait le texte propre sans les pubs ni les menus, puis résume.",
              ],
              [
                "Historique persistant + recherche",
                "Vos 200 résumés de la semaine restent classés, recherchables, exportables. ChatGPT vous garde 3 conversations en pagaille — vous, vous avez besoin d'une vraie bibliothèque.",
              ],
              [
                "Le prix étudiant qui n'en est pas un",
                "Pas d'abonnement à 20 €/mois. Pas de « pack étudiant » à négocier. Dès 2 € les 20 résumés, crédits sans expiration. Le prix d'un café.",
              ],
            ].map(([h, body]) => (
              <li
                key={h}
                className="flex items-start gap-3 py-3 border-b border-border last:border-b-0"
              >
                <Check className="w-4 h-4 text-[#002FA7] mt-1 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-base mb-1">{h}</div>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    {body}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ — targeted at student objections */}
      <section
        className="border-b border-border bg-secondary/30"
        data-testid="etudiants-faq"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 lg:py-20">
          <div className="sux-marker mb-3">04 · VOS QUESTIONS</div>
          <h2
            className="text-3xl md:text-4xl font-black tracking-tight mb-8 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Les questions qu'on nous pose le plus.
          </h2>

          <div className="space-y-6">
            {[
              [
                "Est-ce que je peux résumer un livre protégé par le droit d'auteur ?",
                "Oui, pour votre usage personnel — étudier, réviser, ficher. SUX1 ne stocke rien qui puisse être republié. C'est exactement l'usage d'une fiche de lecture manuscrite, en plus rapide.",
              ],
              [
                "Le prof va-t-il détecter que j'ai utilisé une IA ?",
                "SUX1 ne rédige pas à votre place. Il vous fait GAGNER LA LECTURE — pas la rédaction. La fiche que vous en tirez, la dissertation que vous écrivez ensuite, restent 100 % votre travail.",
              ],
              [
                "Mes données de recherche sont-elles utilisées pour entraîner l'IA ?",
                "Non. Vos textes ne sont jamais utilisés pour entraîner un modèle. Vous pouvez supprimer un résumé (ou votre compte entier) à tout moment depuis Paramètres.",
              ],
              [
                "Ça marche pour l'anglais ? Le latin ? L'allemand ?",
                "Français, anglais, espagnol, allemand, italien, portugais — auto-détection ou langue forcée. Pour le latin ou d'autres langues rares, SUX1 s'en sort étonnamment bien via Claude Sonnet 4.5.",
              ],
              [
                "Pourquoi des packs de crédits et pas un abonnement mensuel ?",
                "Parce qu'on trouve les abonnements SaaS abusifs. Vous payez le prix d'un café pour 20 résumés, utilisables quand vous voulez : les crédits n'expirent jamais. Besoin de plus ? Packs de 60 (5 €) ou 130 (10 €). Point.",
              ],
            ].map(([q, a]) => (
              <div
                key={q}
                className="border border-border bg-card p-6"
                data-testid={`etudiants-faq-item`}
              >
                <div className="font-semibold text-base mb-2">{q}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="bg-[#002FA7] text-white"
        data-testid="etudiants-cta-final"
      >        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 text-center">
          <div className="sux-marker mb-4 text-white/60">
            05 · COMMENCEZ MAINTENANT
          </div>
          <h2
            className="text-4xl md:text-5xl font-black tracking-tight mb-6 leading-tight"
            style={{ fontFamily: "Outfit" }}
          >
            Dès 2 € les 20.
            <br />
            <span className="text-white/90">Pas de carte demandée.</span>
          </h2>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-10 leading-relaxed">
            Testez sur votre polycopié le plus lourd, votre article le plus
            technique, votre PDF le plus mal scanné. Si vous n'êtes pas
            convaincu, vous n'avez rien à faire — vous ne payez jamais.
          </p>
          <Link
            to={user ? "/" : "/signup"}
            data-testid="etudiants-cta-final-signup"
            className="inline-flex items-center gap-2 bg-white text-[#002FA7] font-mono uppercase tracking-[0.25em] text-xs px-8 py-5 hover:bg-white/90 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Créer mon compte gratuit
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <div className="mt-8">
            <Link
              to="/education"
              data-testid="etudiants-cross-education"
              className="font-mono uppercase tracking-[0.2em] text-[10px] text-white/60 hover:text-white transition-colors"
            >
              Vous êtes une école / université ? Voir la page dédiée →
            </Link>
          </div>
          <div className="mt-6 flex items-center justify-center gap-6 text-xs font-mono uppercase tracking-[0.2em] text-white/50">
            <span className="flex items-center gap-1.5">
              <Layers className="w-3 h-3" />3 IA au choix
            </span>
            <span className="flex items-center gap-1.5">
              <Globe className="w-3 h-3" />6 langues
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" />5 secondes
            </span>
          </div>
        </div>
      </section>

      {/* Footer nav — À propos + Contact (consistent across public pages) */}
      <footer className="py-8 px-6 md:px-10 text-center border-t border-border bg-card">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link
            to="/about"
            data-testid="etudiants-footer-about"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            À propos
          </Link>
          <a
            href="mailto:fiestaman38470@gmail.com?subject=%5BSUX1%5D%20Contact"
            data-testid="etudiants-footer-contact"
            className="font-mono uppercase tracking-[0.2em] text-[10px] text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}
